import type { Payout, PayoutType } from '../types/payout';
import { generateUUID } from './uuid';
import { Sentry } from './sentry';

export const exportPayoutsToCSV = (payouts: Payout[], filename?: string): void => {
  if (payouts.length === 0) {
    alert('Aucun payout à exporter');
    return;
  }

  const headers = [
    'Type',
    'Date',
    'Cryptomonnaie',
    'Symbole',
    'Montant',
    'Prix Unitaire (EUR)',
    'Valeur Totale (EUR)',
    'Crypto envoyée',
    'Montant envoyé',
    'Crypto reçue',
    'Montant reçu',
    'Frais',
    'Plateforme',
    'Date de création'
  ];

  const csvContent = [
    headers.join(','),
    ...payouts.map(payout => {
      const typeLabel = payout.type === 'achat' ? 'Achat'
        : payout.type === 'vente' ? 'Vente'
        : payout.type === 'swap' ? 'Swap'
        : payout.type === 'stacking' ? 'Stacking'
        : 'Payout';

      if (payout.type === 'swap') {
        return [
          typeLabel,
          new Date(payout.date).toLocaleString('fr-FR'),
          '-',
          '-',
          '-',
          '-',
          '-',
          `"${payout.sentCrypto?.name}"`,
          payout.sentAmount?.toFixed(8) || '',
          `"${payout.receivedCrypto?.name}"`,
          payout.receivedAmount?.toFixed(8) || '',
          payout.fees?.toFixed(2) || '',
          payout.platform || '',
          new Date(payout.createdAt).toLocaleString('fr-FR')
        ].join(',');
      }

      return [
        typeLabel,
        new Date(payout.date).toLocaleString('fr-FR'),
        `"${payout.crypto?.name}"`,
        payout.crypto?.symbol || '',
        payout.amount?.toFixed(8) || '',
        payout.unitPrice?.toFixed(8) || '',
        payout.totalValue?.toFixed(2) || '',
        '',
        '',
        '',
        '',
        '',
        '',
        new Date(payout.createdAt).toLocaleString('fr-FR')
      ].join(',');
    })
  ].join('\n');

  const bom = '\uFEFF';
  const blob = new Blob([bom + csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');

  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename || `payouts_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }
};

export const exportPayoutsByMonth = (payouts: Payout[], year: number, month: number): void => {
  const filteredPayouts = payouts.filter(payout => {
    const payoutDate = new Date(payout.date);
    return payoutDate.getFullYear() === year && payoutDate.getMonth() === month;
  });

  const monthName = new Date(year, month).toLocaleDateString('fr-FR', { 
    year: 'numeric', 
    month: 'long' 
  });
  
  exportPayoutsToCSV(filteredPayouts, `payouts_${monthName.replace(' ', '_')}.csv`);
};

// ---------------------------------------------------------------------------
// Export réimportable (format machine, toutes colonnes)
// ---------------------------------------------------------------------------

const IMPORTABLE_HEADERS = [
  'id', 'type', 'date', 'created_at',
  'crypto_id', 'crypto_symbol', 'crypto_name',
  'amount', 'unit_price', 'total_value',
  'sent_crypto_id', 'sent_crypto_symbol', 'sent_crypto_name',
  'sent_amount', 'sent_unit_price', 'sent_total_value',
  'received_crypto_id', 'received_crypto_symbol', 'received_crypto_name',
  'received_amount', 'received_unit_price', 'received_total_value',
  'fees', 'fees_currency', 'platform', 'notes',
] as const;

type ImportableHeader = typeof IMPORTABLE_HEADERS[number];

function escapeCell(value: string | number | undefined | null): string {
  if (value === undefined || value === null) return '';
  const str = String(value);
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

function payoutToImportableRow(p: Payout): string {
  const cells: (string | number | undefined)[] = [
    p.id, p.type, p.date, p.createdAt,
    p.crypto?.id, p.crypto?.symbol, p.crypto?.name,
    p.amount, p.unitPrice, p.totalValue,
    p.sentCrypto?.id, p.sentCrypto?.symbol, p.sentCrypto?.name,
    p.sentAmount, p.sentUnitPrice, p.sentTotalValue,
    p.receivedCrypto?.id, p.receivedCrypto?.symbol, p.receivedCrypto?.name,
    p.receivedAmount, p.receivedUnitPrice, p.receivedTotalValue,
    p.fees, p.feesCurrency, p.platform, p.notes,
  ];
  return cells.map(escapeCell).join(',');
}

export const exportPayoutsImportable = (payouts: Payout[], filename?: string): void => {
  const rows = [
    IMPORTABLE_HEADERS.join(','),
    ...payouts.map(payoutToImportableRow),
  ];
  const csv = '\uFEFF' + rows.join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.href = url;
  link.download = filename ?? `cryptotrack-export-${new Date().toISOString().split('T')[0]}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

// ---------------------------------------------------------------------------
// Template CSV téléchargeable
// ---------------------------------------------------------------------------

const TEMPLATE_EXAMPLES = [
  // payout example
  [
    'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx', 'payout', '2024-01-15T08:30:00.000Z', '2024-01-15T08:30:00.000Z',
    'bitcoin', 'BTC', 'Bitcoin',
    '0.00125000', '42000.00', '52.50',
    '', '', '', '', '', '',
    '', '', '', '', '', '',
    '0.50', 'EUR', 'NiceHash', 'Minage janvier',
  ],
  // stacking example
  [
    'yyyyyyyy-yyyy-yyyy-yyyy-yyyyyyyyyyyy', 'stacking', '2024-02-01T00:00:00.000Z', '2024-02-01T00:00:00.000Z',
    'ethereum', 'ETH', 'Ethereum',
    '0.05000000', '2500.00', '125.00',
    '', '', '', '', '', '',
    '', '', '', '', '', '',
    '', '', 'Lido', '',
  ],
  // swap example
  [
    'zzzzzzzz-zzzz-zzzz-zzzz-zzzzzzzzzzzz', 'swap', '2024-03-10T14:00:00.000Z', '2024-03-10T14:00:00.000Z',
    '', '', '',
    '', '', '',
    'bitcoin', 'BTC', 'Bitcoin', '0.01000000', '60000.00', '600.00',
    'ethereum', 'ETH', 'Ethereum', '0.24000000', '2500.00', '600.00',
    '1.20', 'EUR', 'Kraken', '',
  ],
];

export const downloadCSVTemplate = (): void => {
  const rows = [
    IMPORTABLE_HEADERS.join(','),
    ...TEMPLATE_EXAMPLES.map(row => row.map(escapeCell).join(',')),
  ];
  const csv = '\uFEFF' + rows.join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.href = url;
  link.download = 'cryptotrack-template-import.csv';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

// ---------------------------------------------------------------------------
// Import CSV avec détection de doublons
// ---------------------------------------------------------------------------

export interface CsvImportResult {
  toImport: Payout[];
  duplicates: Payout[];
  invalid: number;
}

function contentKey(p: Payout): string {
  return [
    p.type,
    p.date?.slice(0, 10),
    p.crypto?.id ?? p.sentCrypto?.id ?? '',
    p.amount ?? p.sentAmount ?? '',
  ].join('|');
}

function parseCsvLine(line: string): string[] {
  const cells: string[] = [];
  let current = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') {
      if (inQuotes && line[i + 1] === '"') { current += '"'; i++; }
      else { inQuotes = !inQuotes; }
    } else if (ch === ',' && !inQuotes) {
      cells.push(current); current = '';
    } else {
      current += ch;
    }
  }
  cells.push(current);
  return cells;
}

/**
 * Normalize external type aliases to internal PayoutType values.
 * Maps common CSV export formats (e.g. from blockchain explorers) to app types.
 */
function normalizeType(raw: string): string {
  const aliases: Record<string, string> = {
    deposit: 'payout',
    withdrawal: 'transfert',
  };
  return aliases[raw.toLowerCase()] ?? raw;
}

function rowToPayout(row: Record<ImportableHeader, string>): Payout | null {
  try {
    const type = normalizeType(row.type) as PayoutType;
    if (!['payout', 'achat', 'stacking', 'vente', 'swap', 'transfert'].includes(type)) return null;
    if (!row.date) return null;
    const num = (v: string) => v !== '' ? parseFloat(v) : undefined;
    return {
      id: row.id || generateUUID(),
      type,
      date: row.date,
      createdAt: row.created_at || new Date().toISOString(),
      crypto: row.crypto_id
        ? { id: row.crypto_id, symbol: row.crypto_symbol, name: row.crypto_name }
        : undefined,
      amount: num(row.amount),
      unitPrice: num(row.unit_price),
      totalValue: num(row.total_value),
      sentCrypto: row.sent_crypto_id
        ? { id: row.sent_crypto_id, symbol: row.sent_crypto_symbol, name: row.sent_crypto_name }
        : undefined,
      sentAmount: num(row.sent_amount),
      sentUnitPrice: num(row.sent_unit_price),
      sentTotalValue: num(row.sent_total_value),
      receivedCrypto: row.received_crypto_id
        ? { id: row.received_crypto_id, symbol: row.received_crypto_symbol, name: row.received_crypto_name }
        : undefined,
      receivedAmount: num(row.received_amount),
      receivedUnitPrice: num(row.received_unit_price),
      receivedTotalValue: num(row.received_total_value),
      fees: num(row.fees),
      feesCurrency: row.fees_currency || undefined,
      platform: row.platform || undefined,
      notes: row.notes || undefined,
    };
  } catch {
    return null;
  }
}

export const parsePayoutsFromCSV = (
  csvText: string,
  existingPayouts: Payout[]
): CsvImportResult => {
  try {
    const text = csvText.replace(/^\uFEFF/, '');
    const lines = text.split(/\r?\n/).filter(l => l.trim() !== '');
    if (lines.length < 2) return { toImport: [], duplicates: [], invalid: 0 };

    const headers = parseCsvLine(lines[0]) as ImportableHeader[];

    const requiredCols: ImportableHeader[] = ['type', 'date'];
    for (const col of requiredCols) {
      if (!headers.includes(col)) throw new Error(`Colonne obligatoire manquante : "${col}"`);
    }

    const existingIds = new Set(existingPayouts.map(p => p.id));
    const existingKeys = new Set(existingPayouts.map(contentKey));

    const toImport: Payout[] = [];
    const duplicates: Payout[] = [];
    let invalid = 0;

    for (let i = 1; i < lines.length; i++) {
      const cells = parseCsvLine(lines[i]);
      const row = Object.fromEntries(
        headers.map((h, idx) => [h, cells[idx]?.trim() ?? ''])
      ) as Record<ImportableHeader, string>;

      const payout = rowToPayout(row);
      if (!payout) { invalid++; continue; }

      if (payout.id && existingIds.has(payout.id)) {
        duplicates.push(payout); continue;
      }
      if (existingKeys.has(contentKey(payout))) {
        duplicates.push(payout); continue;
      }

      toImport.push(payout);
      existingIds.add(payout.id);
      existingKeys.add(contentKey(payout));
    }

    return { toImport, duplicates, invalid };
  } catch (error) {
    Sentry.captureException(error, { tags: { operation: 'parsePayoutsFromCSV' } });
    throw error;
  }
};